import React from 'react';
import Card from './Card';
import CardHeader from './CardHeader';
import CardContent from './CardContent';
import CardActions from './CardActions';

export interface ListItem {
  id: string;
  primary: string;
  secondary?: string;
  tertiary?: string;
  icon?: React.ReactNode;
  selected?: boolean;
  actions?: React.ReactNode;
}

export interface ListCardProps {
  title?: string;
  subtitle?: string;
  items: ListItem[];
  onItemClick?: (item: ListItem) => void;
  _onItemAction?: (itemId: string, action: string) => void;
  headerActions?: React.ReactNode;
  footerActions?: React.ReactNode;
  loading?: boolean;
  emptyState?: {
    icon: React.ReactNode;
    title: string;
    description?: string;
    action?: React.ReactNode;
  };
  maxHeight?: string;
  selectable?: boolean;
  className?: string;
}

const ListCard: React.FC<ListCardProps> = ({
  title,
  subtitle,
  items,
  onItemClick,
  _onItemAction,
  headerActions,
  footerActions,
  loading = false,
  emptyState,
  maxHeight = 'max-h-96',
  selectable = true,
  className = '',
}) => {
  return (
    <Card variant="content" size="full" hover={false} loading={loading} className={className}>
      {/* Header */}
      {(title || subtitle || headerActions) && (
        <CardHeader title={title} subtitle={subtitle} actions={headerActions} border={true} />
      )}

      {/* List Content */}
      <CardContent padding={false} scroll={true} maxHeight={maxHeight}>
        {items.length === 0 && emptyState ? (
          <div className="flex items-center justify-center py-12 px-6">
            <div className="text-center">
              <div className="w-12 h-12 text-gray-300 mx-auto mb-4">{emptyState.icon}</div>
              <h3 className="text-lg onyx-subtitle text-gray-900 mb-2">{emptyState.title}</h3>
              {emptyState.description && (
                <p className="text-gray-500 onyx-body mb-4">{emptyState.description}</p>
              )}
              {emptyState.action}
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {items.map((item) => (
              <div
                key={item.id}
                className={`p-4 transition-colors ${
                  selectable && onItemClick ? 'cursor-pointer hover:bg-gray-50' : ''
                } ${item.selected ? 'bg-blue-50 border-r-4 border-onyx-bright-blue' : ''}`}
                onClick={() => onItemClick?.(item)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    {item.icon && (
                      <div className="w-5 h-5 text-gray-400 flex-shrink-0">{item.icon}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="onyx-subtitle text-gray-900 truncate">{item.primary}</p>
                      {item.secondary && (
                        <p className="text-sm text-gray-500 onyx-body truncate">{item.secondary}</p>
                      )}
                      {item.tertiary && (
                        <p className="text-xs text-gray-400 onyx-body truncate">{item.tertiary}</p>
                      )}
                    </div>
                  </div>

                  {item.actions && (
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">{item.actions}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Footer Actions */}
      {footerActions && <CardActions border={true}>{footerActions}</CardActions>}
    </Card>
  );
};

export default ListCard;
